/**
 * InboundActivation — Account-Sektion „Empfangspostfach aktivieren" (WL-A4).
 *
 * AVV-Gate VOR Adressvergabe ([I-4]): Slug-Vorschlag/-Eingabe mit Live-
 * Verfügbarkeitsprüfung, Anzeige des versionierten AVV-Dokuments, UNVORBELEGTE
 * Zustimmungs-Checkbox (Kopplungsverbot — keine Vorbelegung), Aktivieren-Button.
 * Erfolg loggt die Zustimmung server-seitig (Consent) und vergibt Slug + Mailbox.
 *
 * Fail-closed an drei Stellen:
 *   1. `canUseInbound(effectiveScopeUid)` false (weder Flag noch Beta-Tenant-
 *      Allowlist, s. config/inboundAccess.ts) → rendert `null` (inert).
 *   2. AVV-Datei fehlt/Entwurf → „AVV noch nicht freigegeben", Aktivierung gesperrt.
 *   3. §203-Berufsgeheimnisträger → nur bei `REACT_APP_S203_INBOUND_AVAILABLE`
 *      sichtbar; sonst KMU-Default-Pfad (Kanzlei-Aktivierung gesperrt).
 *
 * Die AVV-Datei (`public/legal/avv-inbound-v1.md`) legt der Operator an (J-3
 * Legal-Gate) — diese Komponente LIEST sie nur, erstellt/ändert sie nie.
 *
 * Muster: ApiAccessSection / EmailPreferencesSection in AccountPage.tsx.
 *
 * Styling (Dark-Fix, siehe changelog): `AccountPage.tsx` rendert seinen
 * äußeren Container fest als bg-surface / text-primary (KEIN
 * OS/Browser-Dark-Mode, sondern eine permanent dunkle Seite — genau wie
 * `InboxPage.tsx` und die Nachbar-Sektion `ApiAccessSection.tsx` auf
 * derselben Seite). Alle Klassen hier folgen deshalb bewusst der bereits
 * etablierten dunklen Palette dieser Seite/dieses Moduls (ApiAccessSection,
 * InboundBadgeList-Tones, InboundSetupGuide) statt einem eigenen
 * Light-Card-Look — sonst entsteht genau der gemeldete Weiß-auf-Weiß-Bug
 * (unstyled `<input>` erbt `text-white` vom Seiten-Container, während der
 * native Input-Hintergrund weiß bleibt).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMailOpen, IconSuccess, IconWarning } from '../../icons';
import { Seller } from '../../types/invoiceTypes';
import logger from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import {
  canUseInbound,
  S203_INBOUND_AVAILABLE_FLAG,
  INBOUND_AVV_VERSION,
  INBOUND_AVV_DOC_PATH,
  S203_INBOUND_AVV_DOC_PATH,
  INBOUND_AVV_DRAFT_MARKER,
} from '../../config/inboundAccess';
import { useLegalDocument } from '../tenant/useLegalDocument';
import {
  checkInboundSlug,
  provisionInboundAddress,
  getOwnInboundMailbox,
  normalizeCallableError,
  SlugAvailability,
  InboundMailboxState,
} from '../../api/inboundApi';
import { takeRecruitRef, claimRecruitedInvoice } from '../../api/recruitApi';
import SimpleMarkdown from './SimpleMarkdown';

// ---------------------------------------------------------------------------
// Pure slug suggestion (FE-Vorbelegung; die AUTORITATIVE Prüfung macht der
// Server-dryRun in `checkInboundSlug`). Spiegelt bewusst `normalizeSlug` aus
// functions/src/inbound/provisioning.ts — package-Grenze verhindert Import.
// ---------------------------------------------------------------------------

/** Normalisiert einen Firmennamen zu einem Slug-Vorschlag (§5.4). */
export function suggestSlug(name: string | null | undefined): string {
  if (typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
    .slice(0, 30);
}

/** Client-seitiges Format-Gate (Spiegel von SLUG_RE) — nur für sofortiges UI-Feedback. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{2,29}$/;

interface InboundActivationProps {
  sellerData: Seller | null;
}

/** Zustand der Live-Verfügbarkeitsprüfung. */
type AvailabilityState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'result'; value: SlugAvailability }
  | { kind: 'error' };

/** Zustand des AVV-Dokument-Ladens. */
type AvvDocState =
  | { kind: 'loading' }
  | { kind: 'ready'; text: string }
  | { kind: 'draft'; text: string }
  | { kind: 'missing' };

/** Zustand des automatischen Recruit-Claims nach Provisionierung (WL-V3). */
type ClaimState =
  | { kind: 'idle' }
  | { kind: 'claiming' }
  | { kind: 'claimed' }
  | { kind: 'failed' };

const InboundActivation: React.FC<InboundActivationProps> = ({ sellerData }) => {
  // Beta-Gate (Flag ODER Beta-Tenant-Allowlist, s. config/inboundAccess.ts):
  // solange weder greift → komplett inert (kein DOM). Muss VOR jedem weiteren
  // Hook-Aufruf entscheiden? Nein — React verbietet bedingte Hooks. Daher:
  // Outer-Gate rendert die Inner-Komponente nur bei aktivem Zugriff (kein Hook
  // aus der Inner-Komponente läuft, wenn der Gate zu ist). `useAuth()` selbst
  // ist hier unbedingt aufgerufen (kein Rules-of-Hooks-Verstoß).
  const { effectiveScopeUid } = useAuth();
  if (!canUseInbound(effectiveScopeUid)) {
    return null;
  }
  return <InboundActivationInner sellerData={sellerData} />;
};

const InboundActivationInner: React.FC<InboundActivationProps> = ({ sellerData }) => {
  const { t } = useTranslation('inbound');

  // Ergänzender §203-AVV-Text (Berufsgeheimnisträger). Unbedingt geladen
  // (Rules-of-Hooks) — angezeigt wird er aber nur im §203-Zweig unten, damit der
  // Nutzer die ergänzenden Bedingungen VOR dem §203-Haken einsehen kann
  // (informierte Einwilligung, jurist-Empfehlung). Fail-closed: solange nicht
  // `ready`, bleibt der §203-Haken gesperrt (analog Basis-AVV).
  const s203Doc = useLegalDocument(S203_INBOUND_AVV_DOC_PATH, INBOUND_AVV_DRAFT_MARKER);

  const [slug, setSlug] = useState<string>('');
  const [availability, setAvailability] = useState<AvailabilityState>({ kind: 'idle' });
  const [avvDoc, setAvvDoc] = useState<AvvDocState>({ kind: 'loading' });
  const [avvAccepted, setAvvAccepted] = useState<boolean>(false); // UNVORBELEGT (Kopplungsverbot)
  const [s203Holder, setS203Holder] = useState<boolean>(false);
  const [s203Accepted, setS203Accepted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [mailbox, setMailbox] = useState<InboundMailboxState | null>(null);
  const [mailboxLoading, setMailboxLoading] = useState<boolean>(true);
  const [claimState, setClaimState] = useState<ClaimState>({ kind: 'idle' });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-claim runs at most once per mount (the token is single-use, cleared on read).
  const claimAttemptedRef = useRef<boolean>(false);
  // Deeplink-Ziel `/dashboard/konto#empfangspostfach` (UnifiedInboxPage
  // "nicht aktiviert"-Hinweis): scrollt beim Mount zur Karte, wenn der Hash
  // passt. Defensiv — darf in Tests/älteren jsdom-Versionen nie werfen.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#empfangspostfach') return;
    try {
      cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      logger.error('InboundActivation: scrollIntoView failed', err);
    }
  }, []);

  // Slug-Vorschlag aus dem Firmennamen (einmalig, wenn noch leer). `Seller.name`
  // ist das Firmennamen-Feld (BT-27) — `companyName` existiert nur auf `Buyer`,
  // nicht auf `Seller` (verwechselt in der Erstfassung, TS2339).
  useEffect(() => {
    const suggestion = suggestSlug(sellerData?.name);
    if (suggestion) setSlug(suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bereits provisioniert? (Read-only Owner-get auf inbound_mailboxes/{uid}.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mb = await getOwnInboundMailbox();
        if (!cancelled) setMailbox(mb);
      } catch (err) {
        logger.error('InboundActivation: mailbox read failed', err);
      } finally {
        if (!cancelled) setMailboxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-Claim (WL-V3 „Magic Moment", §7.7 — rev. 2026-07-04 DECOUPLE): sobald
  // eine Mailbox vorliegt (frisch provisioniert ODER beim Laden vorhanden) UND ein
  // Recruit-Token aus dem Landing-Flow in sessionStorage liegt, wird der Token
  // synchron gebunden (`claimRecruitedInvoice` = NUR der atomare Bind). Die
  // referenzierte Rechnung des Absenders landet ASYNCHRON (Server-Task
  // `deliverClaimedInvoice`) im eigenen Posteingang — die FE weiß NICHT, ob/wann
  // die Zustellung tatsächlich abgeschlossen ist (kein Status-Polling in v1),
  // daher ist der Erfolgstext bewusst async-ehrlich + trägt den Upload-Hinweis
  // (A-1). `takeRecruitRef` liest+entfernt den Token (Single-Use, kein
  // Doppel-Claim). Der Claim ist best-effort — schlägt der Bind fehl, bleibt die
  // Aktivierung selbst gültig (nur ein Hinweis, kein Block).
  useEffect(() => {
    if (!mailbox || claimAttemptedRef.current) return;
    const token = takeRecruitRef();
    if (!token) return;
    claimAttemptedRef.current = true;
    setClaimState({ kind: 'claiming' });
    let cancelled = false;
    claimRecruitedInvoice(token)
      .then(() => {
        // Bind accepted — delivery is queued async (Phase 2, §7.7). No
        // synchronous confirmation that the document has landed yet.
        if (!cancelled) setClaimState({ kind: 'claimed' });
      })
      .catch((err) => {
        logger.error('InboundActivation: auto-claim (bind) failed', err);
        if (!cancelled) setClaimState({ kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [mailbox]);

  // AVV-Dokument laden (fail-closed bei 404 / Entwurf).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(INBOUND_AVV_DOC_PATH, { cache: 'no-cache' });
        if (!res.ok) {
          if (!cancelled) setAvvDoc({ kind: 'missing' });
          return;
        }
        const text = await res.text();
        // Ein SPA liefert bei unbekannten Pfaden i.d.R. index.html (200) aus —
        // daher zusätzlich auf HTML prüfen und dann als „fehlend" behandeln.
        const looksLikeHtml = /<!doctype html|<html/i.test(text.slice(0, 200));
        if (looksLikeHtml || text.trim() === '') {
          if (!cancelled) setAvvDoc({ kind: 'missing' });
          return;
        }
        const isDraft = text.includes(INBOUND_AVV_DRAFT_MARKER);
        if (!cancelled) {
          setAvvDoc(isDraft ? { kind: 'draft', text } : { kind: 'ready', text });
        }
      } catch (err) {
        logger.error('InboundActivation: AVV fetch failed', err);
        if (!cancelled) setAvvDoc({ kind: 'missing' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced Live-Verfügbarkeitsprüfung (Server-dryRun = autoritativ).
  const runAvailabilityCheck = useCallback((candidate: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!SLUG_RE.test(candidate)) {
      setAvailability(
        candidate.length === 0
          ? { kind: 'idle' }
          : { kind: 'result', value: { available: false, reason: 'invalid' } },
      );
      return;
    }
    setAvailability({ kind: 'checking' });
    debounceRef.current = setTimeout(async () => {
      try {
        const value = await checkInboundSlug(candidate);
        setAvailability({ kind: 'result', value });
      } catch (err) {
        logger.error('InboundActivation: availability check failed', err);
        setAvailability({ kind: 'error' });
      }
    }, 400);
  }, []);

  useEffect(() => {
    runAvailabilityCheck(slug);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [slug, runAvailabilityCheck]);

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Nur erlaubte Zeichen durchlassen (sofortiges Feedback), Rest normalisiert der Server.
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(raw.slice(0, 30));
    setErrorCode(null);
  };

  const avvAvailable = avvDoc.kind === 'ready';
  const slugAvailable =
    availability.kind === 'result' && availability.value.available === true;

  // §203-AVV muss lesbar (`ready`) vorliegen, bevor der Berufsgeheimnisträger
  // zustimmen kann — informierte Einwilligung, fail-closed (analog `avvAvailable`).
  const s203DocReady = s203Doc.kind === 'ready';

  // §203-Selbstauskunft: Frage nur bei aktivem Flag. Ist der Nutzer
  // Berufsgeheimnisträger, muss die §203-Zustimmung zusätzlich gesetzt sein UND
  // der ergänzende §203-AVV-Text lesbar geladen sein (sonst gesperrt).
  const s203Blocking =
    s203Holder && (!S203_INBOUND_AVAILABLE_FLAG || !s203Accepted || !s203DocReady);

  const canActivate =
    avvAvailable &&
    avvAccepted &&
    slugAvailable &&
    !s203Blocking &&
    !submitting &&
    !mailbox;

  const handleActivate = async () => {
    if (!canActivate) return;
    setSubmitting(true);
    setErrorCode(null);
    try {
      const result = await provisionInboundAddress({
        slug,
        avvVersion: INBOUND_AVV_VERSION,
        isProfessionalSecrecyHolder: s203Holder,
        ...(s203Holder ? { s203Accepted } : {}),
      });
      setMailbox({ slug: result.slug, address: result.address, status: 'active' });
    } catch (err) {
      const norm = normalizeCallableError(err);
      logger.error('InboundActivation: provisioning failed', norm);
      // Bevorzugt den serverseitigen Reason-Token (z.B. 'slug_taken',
      // 'mailbox_exists') aus der Fehlermeldung; sonst den groben Callable-Code.
      const reason =
        norm.message && /^[a-z_]+$/.test(norm.message)
          ? norm.message
          : norm.code ?? 'internal';
      setErrorCode(reason);
      // Bei Slug-Kollision Verfügbarkeit neu prüfen.
      if (reason === 'slug_taken') runAvailabilityCheck(slug);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render ----
  // Dunkle Palette bewusst identisch zu ApiAccessSection.tsx (gleiche Seite)
  // und den InboundBadgeList-Tones (gleiches Modul) — siehe Kommentar oben.

  const cardClass =
    'mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6';

  // Bereits aktiviert → Bestätigungs-Sicht.
  if (mailbox) {
    return (
      <div ref={cardRef} id="empfangspostfach" className={cardClass} data-testid="inbound-activation">
        <div className="flex items-center gap-3 mb-2">
          <IconMailOpen className="text-accent-ink" />
          <h2 className="text-lg font-semibold text-primary">{t('title')}</h2>
        </div>
        <div className="flex items-start gap-2 mt-4 rounded-md bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] p-4">
          <IconSuccess className="mt-0.5 text-[var(--tone-success-fg)]" />
          <div>
            <p className="font-medium text-[var(--tone-success-fg)]">{t('activatedTitle')}</p>
            <p className="mt-1 text-sm text-[var(--tone-success-fg)]">
              {t('activatedAddress')}{' '}
              <span className="font-mono font-semibold">{mailbox.address}</span>
            </p>
          </div>
        </div>

        {/* Auto-Claim-Hinweis (WL-V3): nur wenn ein Recruit-Token eingelöst wurde. */}
        {claimState.kind === 'claiming' && (
          <p className="mt-3 text-sm text-muted" role="status" data-testid="inbound-claim-status">
            {t('claim.claiming')}
          </p>
        )}
        {claimState.kind === 'claimed' && (
          <div
            className="flex items-start gap-2 mt-3 rounded-md bg-[var(--tone-accent-bg)] border border-[var(--tone-accent-border)] p-4"
            role="status"
            data-testid="inbound-claim-success"
          >
            <IconSuccess className="mt-0.5 text-accent-ink" />
            <div>
              <p className="font-medium text-[var(--tone-accent-fg)]">{t('claim.successTitle')}</p>
              <p className="mt-1 text-sm text-accent-ink">{t('claim.successBody')}</p>
            </div>
          </div>
        )}
        {claimState.kind === 'failed' && (
          <p className="mt-3 text-sm text-muted" role="status" data-testid="inbound-claim-failed">
            {t('claim.failed')}
          </p>
        )}
      </div>
    );
  }

  const avvBlocked = avvDoc.kind === 'missing' || avvDoc.kind === 'draft';

  return (
    <div ref={cardRef} id="empfangspostfach" className={cardClass} data-testid="inbound-activation">
      <div className="flex items-center gap-3 mb-2">
        <IconMailOpen className="text-accent-ink" />
        <h2 className="text-lg font-semibold text-primary">{t('title')}</h2>
      </div>
      <p className="text-sm text-muted mb-4">{t('description')}</p>

      {/* AVV-Fehlt/Entwurf → fail-closed-Banner, Aktivierung gesperrt. */}
      {avvBlocked && (
        <div
          className="flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-4 mb-4"
          role="status"
          data-testid="inbound-avv-blocked"
        >
          <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
          <p className="text-sm text-[var(--tone-warning-fg)]">
            {avvDoc.kind === 'draft' ? t('avvDraft') : t('avvNotReleased')}
          </p>
        </div>
      )}

      {mailboxLoading && (
        <p className="text-sm text-muted">{t('loading')}</p>
      )}

      {!mailboxLoading && (
        <>
          {/* Slug-Eingabe + Live-Verfügbarkeit */}
          <label htmlFor="inbound-slug" className="block text-sm font-medium text-secondary">
            {t('slugLabel')}
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              id="inbound-slug"
              type="text"
              value={slug}
              onChange={handleSlugChange}
              placeholder={t('slugPlaceholder')}
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              className="w-48 rounded-md border border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] px-3 py-2 text-sm font-mono text-primary placeholder:text-faint focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              data-testid="inbound-slug-input"
            />
            <span className="text-sm text-muted font-mono">@rechnung.faebill.com</span>
          </div>

          {/* Verfügbarkeits-Feedback */}
          <div className="mt-1 h-5 text-sm" aria-live="polite" data-testid="inbound-slug-status">
            {availability.kind === 'checking' && (
              <span className="text-muted">{t('checking')}</span>
            )}
            {availability.kind === 'error' && (
              <span className="text-[var(--tone-danger-fg)]">{t('checkError')}</span>
            )}
            {availability.kind === 'result' && availability.value.available && (
              <span className="text-[var(--tone-success-fg)]">{t('available')}</span>
            )}
            {availability.kind === 'result' &&
              !availability.value.available && (
                <span className="text-[var(--tone-danger-fg)]">
                  {t(`unavailable.${availability.value.reason ?? 'invalid'}`)}
                </span>
              )}
          </div>

          {/* §203-Selbstauskunft — Frage immer, Aktivierbarkeit nur bei Flag. */}
          <div className="mt-4">
            <label className="flex items-start gap-2 text-sm text-secondary">
              <input
                type="checkbox"
                checked={s203Holder}
                onChange={(e) => {
                  setS203Holder(e.target.checked);
                  if (!e.target.checked) setS203Accepted(false);
                }}
                className="mt-0.5 rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500"
                data-testid="inbound-s203-holder"
              />
              <span>{t('s203Question')}</span>
            </label>

            {s203Holder && !S203_INBOUND_AVAILABLE_FLAG && (
              <div
                className="mt-2 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-3"
                role="status"
                data-testid="inbound-s203-locked"
              >
                <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
                <p className="text-sm text-[var(--tone-warning-fg)]">{t('s203Locked')}</p>
              </div>
            )}

            {s203Holder && S203_INBOUND_AVAILABLE_FLAG && (
              <div className="mt-3">
                {/* Ergänzender §203-AVV-Text — sichtbar VOR der Zustimmung
                    (informierte Einwilligung). Scrollbare Anzeige + prominenter
                    Öffnen-Link. Muster: Basis-AVV-Anzeige unten. */}
                <h4 className="text-sm font-medium text-secondary mb-1">{t('s203AvvHeading')}</h4>
                <div
                  className="max-h-48 overflow-y-auto rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
                  data-testid="inbound-s203-avv-doc"
                >
                  {s203Doc.kind === 'ready' || s203Doc.kind === 'draft' ? (
                    <SimpleMarkdown text={s203Doc.text} />
                  ) : (
                    t('s203AvvNotReleased')
                  )}
                </div>
                <a
                  href={S203_INBOUND_AVV_DOC_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-xs text-accent-ink underline"
                  data-testid="inbound-s203-avv-link"
                >
                  {t('s203AvvLinkText')}
                </a>

                <label className="mt-3 flex items-start gap-2 text-sm text-secondary">
                  <input
                    type="checkbox"
                    checked={s203Accepted}
                    disabled={!s203DocReady}
                    onChange={(e) => setS203Accepted(e.target.checked)}
                    className="mt-0.5 rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
                    data-testid="inbound-s203-accept"
                  />
                  <span>{t('s203CheckboxLabel')}</span>
                </label>
              </div>
            )}
          </div>

          {/* F1-A (jurist GA-Haftung, Textbaustein F1-A): Nutzungshinweis Empfangspostfach —
              oberhalb der AVV-Zustimmung. Bündelt Nutzer-Verantwortung, reine
              Auftragsverarbeiter-Rolle, „keine inhaltliche/steuerliche Prüfung" und die
              Fehlbarkeit der automatischen Auslese. */}
          <div
            className="mt-4 rounded-md border border-hairline bg-surface-raised p-3 text-sm text-secondary"
            data-testid="inbound-usage-notice"
          >
            {t('usageNotice')}
          </div>

          {/* AVV-Anzeige + unvorbelegte Zustimmung */}
          <div className="mt-4">
            <h3 className="text-sm font-medium text-secondary mb-1">{t('avvHeading')}</h3>
            <div
              className="max-h-48 overflow-y-auto rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
              data-testid="inbound-avv-doc"
            >
              {avvDoc.kind === 'ready' || avvDoc.kind === 'draft' ? (
                <SimpleMarkdown text={avvDoc.text} />
              ) : (
                t('avvNotReleased')
              )}
            </div>

            <label className="mt-3 flex items-start gap-2 text-sm text-secondary">
              <input
                type="checkbox"
                checked={avvAccepted}
                disabled={!avvAvailable}
                onChange={(e) => setAvvAccepted(e.target.checked)}
                className="mt-0.5 rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
                data-testid="inbound-avv-accept"
              />
              <span>{t('avvCheckboxLabel')}</span>
            </label>
          </div>

          {/* Fehler-Feedback */}
          {errorCode && (
            <p className="mt-3 text-sm text-[var(--tone-danger-fg)]" data-testid="inbound-error">
              {t(`errors.${errorCode}`, { defaultValue: t('errors.internal') })}
            </p>
          )}

          {/* Aktivieren */}
          <button
            type="button"
            onClick={handleActivate}
            disabled={!canActivate}
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-indigo-700"
            data-testid="inbound-activate"
          >
            {submitting ? t('activating') : t('activateButton')}
          </button>
        </>
      )}
    </div>
  );
};

export default InboundActivation;
